import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { TRAINING_COACHES } from "@/lib/constants";

const inputCls =
  "bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight h-11";

export function TrainingForm({ defaultCoach = "", onDone, className = "" }) {
  const [form, setForm] = useState({
    coach: defaultCoach,
    training_type: "Private Training",
    name: "",
    parent_name: "",
    email: "",
    phone: "",
    player_age: "",
    preferred_times: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/submissions", { category: "training", ...form });
      setDone(true);
      toast.success("Training inquiry sent!", { description: data.message });
    } catch (err) {
      toast.error("Could not send inquiry", { description: formatApiError(err.response?.data?.detail) });
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="training-success" className={`text-center py-8 ${className}`}>
        <CheckCircle2 className="mx-auto text-blaze-orange" size={54} />
        <h3 className="mt-4 font-heading text-3xl tracking-wide text-white">Inquiry Received!</h3>
        <p className="mt-2 text-white/70 max-w-md mx-auto">
          Thanks for reaching out. A CT BLAZERS coach will contact you soon to talk through availability and next steps.
        </p>
        <Button
          data-testid="training-done-btn"
          onClick={() => (onDone ? onDone() : setDone(false))}
          className="mt-6 bg-blaze-blue hover:bg-blaze-blueLight text-white font-bold uppercase tracking-wide rounded-full px-8"
        >
          Done
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="training-form" className={`space-y-5 ${className}`}>
      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Select a Coach</Label>
          <Select value={form.coach} onValueChange={set("coach")}>
            <SelectTrigger data-testid="training-coach" className={inputCls}>
              <SelectValue placeholder="Choose a coach" />
            </SelectTrigger>
            <SelectContent className="bg-ink-800 border-white/15 text-white">
              {TRAINING_COACHES.map((c) => (
                <SelectItem key={c} value={c} className="focus:bg-blaze-blue/30">
                  {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Training Type</Label>
          <Select value={form.training_type} onValueChange={set("training_type")}>
            <SelectTrigger data-testid="training-type" className={inputCls}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-ink-800 border-white/15 text-white">
              <SelectItem value="Private Training" className="focus:bg-blaze-blue/30">Private Training</SelectItem>
              <SelectItem value="Small Group Training" className="focus:bg-blaze-blue/30">Small Group Training</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Player Name *</Label>
          <Input required data-testid="training-name" className={inputCls} value={form.name} onChange={set("name")} placeholder="Player full name" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Parent / Guardian Name</Label>
          <Input data-testid="training-parent" className={inputCls} value={form.parent_name} onChange={set("parent_name")} placeholder="If under 18" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Email *</Label>
          <Input required type="email" data-testid="training-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Phone</Label>
          <Input data-testid="training-phone" className={inputCls} value={form.phone} onChange={set("phone")} placeholder="(203) 555-0123" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Player Age / Grade</Label>
          <Input data-testid="training-age" className={inputCls} value={form.player_age} onChange={set("player_age")} placeholder="e.g. 12U / 6th grade" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-white/80 text-xs uppercase tracking-wider">Preferred Days / Times</Label>
          <Input data-testid="training-times" className={inputCls} value={form.preferred_times} onChange={set("preferred_times")} placeholder="e.g. Weeknights, Sunday AM" />
        </div>
      </div>

      <div className="space-y-1.5">
        <Label className="text-white/80 text-xs uppercase tracking-wider">Additional Information</Label>
        <Textarea data-testid="training-message" className="bg-ink-900/60 border-white/15 text-white placeholder:text-white/35 focus-visible:ring-blaze-blueLight min-h-[110px]" value={form.message} onChange={set("message")} placeholder="Goals, areas to work on, or anything else we should know." />
      </div>

      <Button
        type="submit"
        disabled={loading}
        data-testid="training-submit"
        className="w-full bg-blaze-orange hover:bg-blaze-orangeBright text-ink-900 font-bold uppercase tracking-wide rounded-full h-12 text-base shadow-[0_8px_28px_rgba(246,140,31,0.35)] transition-all"
      >
        {loading ? <Loader2 className="animate-spin" /> : "Submit Training Inquiry"}
      </Button>
      <p className="text-center text-xs text-white/40">
        This is an inquiry — a coach will follow up to confirm availability. No payment required now.
      </p>
    </form>
  );
}
